import { Location } from '@angular/common';
import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { AwscontrolService, UploadResponse, ImageUploadResponse } from 'app/services/aws/awscontrol.service';

import { SousBien } from 'app/models/sousBien';
import { BiensImmobsBiensImmobs } from 'app/services/biens-immobs/biens-immobs.service';
import { SousBienService } from 'app/services/sousBiens/sous-bien.service';

type MainUploadMode = 'direct' | 'imagesousproduit';
type GalleryUploadMode = 'direct' | 'imagesousproduit';

@Component({
  selector: 'app-ajouter-sous-biens',
  templateUrl: './ajouter-sous-biens.component.html',
  styleUrls: ['./ajouter-sous-biens.component.scss']
})
export class AjouterSousBiensComponent implements OnInit {
  // ============================================================
  // ✅ RÉFÉRENCES AUX ÉDITEURS contenteditable (WYSIWYG)
  // ============================================================
  @ViewChild('descFrEditor') descFrEditor?: ElementRef<HTMLElement>;
  @ViewChild('descArEditor') descArEditor?: ElementRef<HTMLElement>;
  @ViewChild('descEnEditor') descEnEditor?: ElementRef<HTMLElement>;
  // ============================================================

  public sousBiensadd: any;
  public bien: any;
  public idBien: any;
  public agencyId: string | null = null;
  public sousBien: SousBien;
  public selectedFiles: FileList | null = null;
  public fileNames: string[] = [];
  public isUploading: boolean = false;
  public uploadProgress: number = 0;

  // ==================== MAIN IMAGE ====================
  public uploadedSingleImageUrl: string = '';
  mainUploadMode: MainUploadMode = 'direct';
  mainPreviewOriginalUrl: string | null = null;
  mainPreviewProcessedUrl: string | null = null;
  isAwaitingMainValidation: boolean = false;
  uploadingMain: boolean = false;

  // ==================== GALLERY ====================
  public uploadedImagesList: string[] = [];
  galleryUploadMode: GalleryUploadMode = 'direct';
  galleryPreviewOriginalUrls: string[] = [];
  galleryPreviewProcessedUrls: string[] = [];
  isAwaitingGalleryValidation: boolean = false;
  uploadingGallery: boolean = false;

  // Loading states
  public isLoadingBien: boolean = false;
  public isSubmitting: boolean = false;

  // ====== Language flags ======
  public availableLanguages: string[] = [];
  public showArabicFields: boolean = false;
  public showEnglishFields: boolean = false;

  // Getters/setters pour l'AR et l'EN
  get descriptionArabe(): string {
    return this.sousBien.descriptionArabe || '';
  }
  set descriptionArabe(value: string) {
    this.sousBien.descriptionArabe = value;
  }

  get descriptionAnglais(): string {
    return this.sousBien.descriptionAnglais || '';
  }
  set descriptionAnglais(value: string) {
    this.sousBien.descriptionAnglais = value;
  }

  constructor(
    private bienService: BiensImmobsBiensImmobs,
    private sousBienService: SousBienService,
    private awsService: AwscontrolService,
    private router: Router,
    private _location: Location,
    private activatedRoute: ActivatedRoute
  ) {
    this.sousBien = new SousBien();
    this.sousBien.description = '';
    this.sousBien.descriptionArabe = '';
    this.sousBien.descriptionAnglais = '';
  }

  ngOnInit(): void {
    this.getData();
  }

  getData(): void {
    this.idBien = this.activatedRoute.snapshot.paramMap.get('id');
    if (this.idBien) {
      this.isLoadingBien = true;
      this.bienService.getBiensImmobs(this.idBien).subscribe(
        (data: any) => {
          console.log("Fetched Bien data:", data);
          this.bien = data;

          if (data.user?.id) {
            this.agencyId = data.user.id.toString();
          } else if (data.userId) {
            this.agencyId = data.userId.toString();
          } else if (data.agencyId) {
            this.agencyId = data.agencyId.toString();
          }

          this.availableLanguages =
            data.user?.availableLanguages ||
            data.availableLanguages ||
            [];

          this.showArabicFields = this.availableLanguages.includes('AR');
          this.showEnglishFields = this.availableLanguages.includes('EN');

          this.isLoadingBien = false;

          // 🔥 Remplir les éditeurs après rendu
          this.applyRichTextAfterRender();
        },
        error => {
          console.error("Error fetching Bien data:", error);
          this.isLoadingBien = false;
          Swal.fire({
            icon: "error",
            title: "Erreur",
            text: "Impossible de charger les données du bien.",
            confirmButtonColor: '#f44336'
          });
        }
      );
    }
  }

  /* ============================================================
     ✅ SYNC DES ÉDITEURS contenteditable
     ============================================================ */
  private applyRichTextAfterRender(): void {
    setTimeout(() => this.syncEditors(), 0);
    setTimeout(() => this.syncEditors(), 100);
    setTimeout(() => this.syncEditors(), 300);
  }

  private syncEditors(): void {
    if (this.descFrEditor) {
      const html = this.sousBien.description || '';
      if (this.descFrEditor.nativeElement.innerHTML !== html) {
        this.descFrEditor.nativeElement.innerHTML = html;
      }
    }
    if (this.descArEditor) {
      const html = this.sousBien.descriptionArabe || '';
      if (this.descArEditor.nativeElement.innerHTML !== html) {
        this.descArEditor.nativeElement.innerHTML = html;
      }
    }
    if (this.descEnEditor) {
      const html = this.sousBien.descriptionAnglais || '';
      if (this.descEnEditor.nativeElement.innerHTML !== html) {
        this.descEnEditor.nativeElement.innerHTML = html;
      }
    }
  }

  /* ============================================================
     ✅ HANDLERS (input) DES CONTENTEDITABLE
     ============================================================ */
  onDescFrInput(): void {
    if (this.descFrEditor) {
      this.sousBien.description = this.descFrEditor.nativeElement.innerHTML;
    }
  }

  onDescArInput(): void {
    if (this.descArEditor) {
      this.sousBien.descriptionArabe = this.descArEditor.nativeElement.innerHTML;
    }
  }

  onDescEnInput(): void {
    if (this.descEnEditor) {
      this.sousBien.descriptionAnglais = this.descEnEditor.nativeElement.innerHTML;
    }
  }

  /* ============================================================
     BOLD — seule action de rich text exposée
     ============================================================ */
  makeBold(): void {
    try {
      document.execCommand('bold', false);
    } catch (e) {
      console.warn('execCommand("bold") failed:', e);
    }
  }

  // ==================== HELPERS ====================
  public getRandomString(length: number): string {
    const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
    }
    return result;
  }

  private validateImageFile(file: File): { valid: boolean; error?: string } {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Le fichier ${file.name} n'est pas une image valide. Formats acceptés: JPG, PNG, GIF, WebP.`
      };
    }

    if (file.size > 5 * 1024 * 1024) {
      return {
        valid: false,
        error: `L'image ${file.name} dépasse 5MB. Taille maximum: 5MB.`
      };
    }

    return { valid: true };
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ============================================================
  // ==================== MAIN IMAGE ============================
  // ============================================================

  setMainUploadMode(mode: MainUploadMode): void {
    if (this.uploadingMain) return;
    if (this.isAwaitingMainValidation) this.cancelMainPreview(true);
    this.mainUploadMode = mode;
  }

  selectFileimg(e: any): void {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const validation = this.validateImageFile(file);

    if (!validation.valid) {
      Swal.fire({
        icon: 'warning',
        title: 'Fichier invalide',
        text: validation.error,
        confirmButtonColor: '#f44336'
      });
      input.value = '';
      return;
    }

    this.fileToDataUrl(file).then(d => { this.mainPreviewOriginalUrl = d; });

    if (this.mainUploadMode === 'direct') {
      this.uploadMainDirect(file, input);
    } else {
      this.uploadMainViaImagesousproduit(file, input);
    }
  }

  private uploadMainDirect(file: File, input: HTMLInputElement): void {
    this.uploadingMain = true;

    const renamedFile = new File(
      [file],
      `sous-bien-main/${this.idBien}/${this.getRandomString(10)}_${file.name}`,
      { type: file.type }
    );

    this.awsService.uploadFile(renamedFile, `sous-bien-main/${this.idBien}`).subscribe({
      next: (response: UploadResponse) => {
        this.uploadingMain = false;
        if (response.success && response.urls.length > 0) {
          this.uploadedSingleImageUrl = response.urls[0];
          this.mainPreviewOriginalUrl = null;
          Swal.fire({
            icon: "success",
            title: "Image principale ajoutée",
            text: `${file.name} a été uploadée avec succès`,
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Échec de l'upload",
            text: response.error || `Échec du téléchargement de ${file.name}.`,
            confirmButtonColor: '#f44336'
          });
          this.mainPreviewOriginalUrl = null;
        }
        input.value = '';
      },
      error: (err: any) => {
        this.uploadingMain = false;
        console.error('Error uploading main image:', err);
        Swal.fire({
          icon: "error",
          title: "Erreur de téléchargement",
          text: `Échec du téléchargement de ${file.name}. Veuillez réessayer.`,
          confirmButtonColor: '#f44336'
        });
        this.mainPreviewOriginalUrl = null;
        input.value = '';
      }
    });
  }

  private uploadMainViaImagesousproduit(file: File, input: HTMLInputElement): void {
    this.uploadingMain = true;
    this.isAwaitingMainValidation = false;
    this.mainPreviewProcessedUrl = null;

    this.awsService.imagesousproduit([file], `sous-bien-main/${this.idBien}`).subscribe({
      next: (response: ImageUploadResponse) => {
        this.uploadingMain = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.mainPreviewProcessedUrl = response.urls[0];
          this.isAwaitingMainValidation = true;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: (response.errors && response.errors[0]) || 'Échec du traitement',
            confirmButtonColor: '#f44336'
          });
          this.mainPreviewOriginalUrl = null;
        }
        input.value = '';
      },
      error: () => {
        this.uploadingMain = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter l\'image principale', confirmButtonColor: '#f44336' });
        this.mainPreviewOriginalUrl = null;
        input.value = '';
      }
    });
  }

  validateMainPreview(): void {
    if (!this.mainPreviewProcessedUrl) return;
    this.uploadedSingleImageUrl = this.mainPreviewProcessedUrl;
    this.mainPreviewOriginalUrl = null;
    this.mainPreviewProcessedUrl = null;
    this.isAwaitingMainValidation = false;
    Swal.fire({ icon: 'success', title: 'Image principale validée', timer: 1500, showConfirmButton: false });
  }

  cancelMainPreview(silent: boolean = false): void {
    const url = this.mainPreviewProcessedUrl;
    const doCancel = () => {
      if (url) this.awsService.deleteFile(url).subscribe({ error: () => {} });
      this.mainPreviewOriginalUrl = null;
      this.mainPreviewProcessedUrl = null;
      this.isAwaitingMainValidation = false;
    };
    if (silent) { doCancel(); return; }
    Swal.fire({
      title: 'Refuser cette image ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }

  removeMainImage(): void {
    if (this.isAwaitingMainValidation) { this.cancelMainPreview(); return; }
    if (!this.uploadedSingleImageUrl) return;

    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Voulez-vous vraiment supprimer l'image principale?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(this.uploadedSingleImageUrl).subscribe({
          next: (response) => {
            if (response.status === 'success') {
              this.uploadedSingleImageUrl = '';
              Swal.fire({
                icon: "success",
                title: "Image supprimée",
                text: "L'image principale a été supprimée avec succès",
                timer: 1500,
                showConfirmButton: false
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Erreur",
                text: response.error || "Échec de la suppression de l'image",
                confirmButtonColor: '#f44336'
              });
            }
          },
          error: (err: any) => {
            console.error('Error deleting main image:', err);
            Swal.fire({
              icon: "error",
              title: "Erreur",
              text: "Échec de la suppression de l'image. Veuillez réessayer.",
              confirmButtonColor: '#f44336'
            });
          }
        });
      }
    });
  }

  // ============================================================
  // ==================== GALERIE ===============================
  // ============================================================

  setGalleryUploadMode(mode: GalleryUploadMode): void {
    if (this.uploadingGallery) return;
    if (this.isAwaitingGalleryValidation) this.cancelGalleryPreview(true);
    this.galleryUploadMode = mode;
  }

  selectFileimg1(e: any): void {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const totalImages = this.uploadedImagesList.length + input.files.length;
    if (totalImages > 30) {
      Swal.fire({
        icon: 'warning',
        title: 'Limite atteinte',
        text: `Maximum 30 images autorisées. Vous avez déjà ${this.uploadedImagesList.length} image(s).`,
        confirmButtonColor: '#f44336'
      });
      input.value = '';
      return;
    }

    const validFiles: File[] = [];
    for (let i = 0; i < input.files.length; i++) {
      const file = input.files[i];
      const validation = this.validateImageFile(file);
      if (!validation.valid) {
        Swal.fire({ icon: 'warning', title: 'Fichier invalide', text: validation.error, confirmButtonColor: '#f44336' });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) { input.value = ''; return; }

    if (this.galleryUploadMode === 'direct') {
      this.uploadGalleryDirect(validFiles, input);
    } else {
      this.galleryPreviewOriginalUrls = [];
      validFiles.forEach(f => {
        this.fileToDataUrl(f).then(d => { this.galleryPreviewOriginalUrls.push(d); });
      });
      this.uploadGalleryViaImagesousproduit(validFiles, input);
    }
  }

  private uploadGalleryDirect(files: File[], input: HTMLInputElement): void {
    this.uploadingGallery = true;
    this.isUploading = true;

    const renamedFiles = files.map(file => {
      const newName = `sous-bien-images/${this.idBien}/${this.getRandomString(10)}_${file.name}`;
      return new File([file], newName, { type: file.type });
    });

    this.awsService.uploadFiles(renamedFiles, `sous-bien-images/${this.idBien}`).subscribe({
      next: (response: UploadResponse) => {
        this.uploadingGallery = false;
        this.isUploading = false;

        if (response.success) {
          this.uploadedImagesList = [...this.uploadedImagesList, ...response.urls];
          Swal.fire({
            icon: "success",
            title: "Images ajoutées",
            text: `${response.count} image(s) uploadée(s) avec succès`,
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Échec de l'upload",
            text: response.error || "Les images n'ont pas pu être uploadées.",
            confirmButtonColor: '#f44336'
          });
        }
        input.value = '';
      },
      error: (err: any) => {
        this.uploadingGallery = false;
        this.isUploading = false;
        console.error('Error uploading images:', err);
        Swal.fire({
          icon: "error",
          title: "Erreur de téléchargement",
          text: "Échec du téléchargement des images. Veuillez réessayer.",
          confirmButtonColor: '#f44336'
        });
        input.value = '';
      }
    });
  }

  private uploadGalleryViaImagesousproduit(files: File[], input: HTMLInputElement): void {
    this.uploadingGallery = true;
    this.isAwaitingGalleryValidation = false;
    this.galleryPreviewProcessedUrls = [];

    this.awsService.imagesousproduit(files, `sous-bien-images/${this.idBien}`).subscribe({
      next: (response: ImageUploadResponse) => {
        this.uploadingGallery = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryPreviewProcessedUrls = response.urls;
          this.isAwaitingGalleryValidation = true;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: (response.errors && response.errors[0]) || 'Échec du traitement',
            confirmButtonColor: '#f44336'
          });
          this.galleryPreviewOriginalUrls = [];
        }
        input.value = '';
      },
      error: () => {
        this.uploadingGallery = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter les images', confirmButtonColor: '#f44336' });
        this.galleryPreviewOriginalUrls = [];
        input.value = '';
      }
    });
  }

  validateGalleryPreview(): void {
    if (this.galleryPreviewProcessedUrls.length === 0) return;
    this.uploadedImagesList.push(...this.galleryPreviewProcessedUrls);
    this.galleryPreviewOriginalUrls = [];
    this.galleryPreviewProcessedUrls = [];
    this.isAwaitingGalleryValidation = false;
    Swal.fire({ icon: 'success', title: 'Images validées', timer: 1500, showConfirmButton: false });
  }

  cancelGalleryPreview(silent: boolean = false): void {
    const urls = [...this.galleryPreviewProcessedUrls];
    const doCancel = () => {
      urls.forEach(u => this.awsService.deleteFile(u).subscribe({ error: () => {} }));
      this.galleryPreviewOriginalUrls = [];
      this.galleryPreviewProcessedUrls = [];
      this.isAwaitingGalleryValidation = false;
    };
    if (silent) { doCancel(); return; }
    Swal.fire({
      title: 'Refuser ces images ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }

  removeImage(index: number): void {
    const imageUrl = this.uploadedImagesList[index];

    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Voulez-vous vraiment supprimer cette image?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(imageUrl).subscribe({
          next: (response) => {
            if (response.status === 'success') {
              this.uploadedImagesList.splice(index, 1);
              Swal.fire({
                icon: "success",
                title: "Image supprimée",
                text: "L'image a été supprimée avec succès",
                timer: 1500,
                showConfirmButton: false
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Erreur",
                text: response.error || "Échec de la suppression de l'image",
                confirmButtonColor: '#f44336'
              });
            }
          },
          error: (err: any) => {
            console.error('Error deleting image:', err);
            Swal.fire({
              icon: "error",
              title: "Erreur",
              text: "Échec de la suppression de l'image. Veuillez réessayer.",
              confirmButtonColor: '#f44336'
            });
          }
        });
      }
    });
  }

  moveImageUp(index: number): void {
    if (index > 0) {
      const temp = this.uploadedImagesList[index];
      this.uploadedImagesList[index] = this.uploadedImagesList[index - 1];
      this.uploadedImagesList[index - 1] = temp;
    }
  }

  moveImageDown(index: number): void {
    if (index < this.uploadedImagesList.length - 1) {
      const temp = this.uploadedImagesList[index];
      this.uploadedImagesList[index] = this.uploadedImagesList[index + 1];
      this.uploadedImagesList[index + 1] = temp;
    }
  }

  // ==================== SUBMIT ====================
  private validateForm(): { valid: boolean; error?: string } {
    if (this.isAwaitingMainValidation) {
      return { valid: false, error: "Veuillez valider ou refuser l'image principale." };
    }
    if (this.isAwaitingGalleryValidation) {
      return { valid: false, error: "Veuillez valider ou refuser les images de la galerie." };
    }
    if (!this.sousBien.name || this.sousBien.name.trim() === '') {
      return { valid: false, error: "Le nom est obligatoire." };
    }
    if (!this.sousBien.description || this.sousBien.description.trim() === '') {
      return { valid: false, error: "La description est obligatoire." };
    }
    if (!this.uploadedSingleImageUrl) {
      return { valid: false, error: "L'image principale est obligatoire." };
    }
    return { valid: true };
  }

  onSubmit(): void {
    // 🔥 Récupérer les valeurs depuis les éditeurs contenteditable
    this.onDescFrInput();
    this.onDescArInput();
    this.onDescEnInput();

    const validation = this.validateForm();

    if (!validation.valid) {
      Swal.fire({
        icon: "warning",
        title: "Informations manquantes",
        text: validation.error,
        confirmButtonColor: '#f44336'
      });
      return;
    }

    this.sousBien.imagesList = this.uploadedImagesList;
    this.sousBien.image_url = this.uploadedSingleImageUrl;

    this.sousBiensadd = {
      name: this.sousBien.name.trim(),
      nameArabe: this.showArabicFields ? (this.sousBien.nameArabe || '') : '',
      nameAnglais: this.showEnglishFields ? (this.sousBien.nameAnglais || '') : '',
      category: this.sousBien.category || '',
      description: this.sousBien.description,
      descriptionArabe: this.showArabicFields ? (this.sousBien.descriptionArabe || '') : '',
      descriptionAnglais: this.showEnglishFields ? (this.sousBien.descriptionAnglais || '') : '',
      adressmap: this.sousBien.adressmap || '',
      imagesList: this.sousBien.imagesList,
      image_url: this.sousBien.image_url,
      prix: this.sousBien.prix || ''
    };

    console.log("Sending data to backend:", this.sousBiensadd);

    this.isSubmitting = true;

    Swal.fire({
      title: 'Création en cours...',
      text: 'Veuillez patienter',
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading(); }
    });

    this.sousBienService.createSousBien(this.sousBiensadd, this.idBien).subscribe(
      (response) => {
        this.isSubmitting = false;
        Swal.close();
        console.log('SousBien added successfully:', response);
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Le sous bien a été créé avec succès!",
          confirmButtonColor: '#4caf50'
        }).then(() => {
          this.router.navigate(['/list-agence-sous-bien', this.idBien]);
        });
      },
      (error) => {
        this.isSubmitting = false;
        Swal.close();
        console.error('Error adding SousBien:', error);

        let errorMessage = 'Échec de la création du sous bien. Veuillez réessayer.';
        if (error.error) {
          if (typeof error.error === 'string') {
            errorMessage = error.error;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        Swal.fire({
          icon: "error",
          title: "Échec de la création",
          text: errorMessage,
          confirmButtonColor: '#f44336'
        });
      }
    );
  }

  goBack(): void {
    if (this.agencyId) {
      this.router.navigate(['/list-agence-biens', this.agencyId]);
    } else {
      this._location.back();
    }
  }
}